import { CREDIT_SCORE_MAX, CREDIT_SCORE_MIN } from "../../config";
import { describeLocation, zip5, zipMatchesLocation } from "../../zipCounty";

const isBlank = (value) => value === undefined || value === null || value === "";
const number = (value) => Number(value);
const isFiniteNumber = (value) => !isBlank(value) && Number.isFinite(number(value));
const isNonNegative = (value) => isFiniteNumber(value) && number(value) >= 0;
const isPositive = (value) => isFiniteNumber(value) && number(value) > 0;
const isIntegerInRange = (value, min, max) => (
  isFiniteNumber(value) && Number.isInteger(number(value)) && number(value) >= min && number(value) <= max
);

const error = (field, message) => ({ field, message });

/**
 * Submit-time validation for the PPE form.
 *
 * Ant InputNumber's min/max props only guide keyboard/stepper input; they do
 * not make a form valid and several fields can still be cleared. Keep the
 * business checks here so every path is blocked before a malformed pricing
 * request or a misleading ratio reaches the API.
 */
export function validatePpeSearch(values = {}) {
  const errors = [];
  const add = (condition, field, message) => {
    if (condition) errors.push(error(field, message));
  };
  const secondLien = values.loanProgram === "heloc" || values.loanProgram === "secondMortgage";
  const purchaseFirstLien = values.loanPurpose === "Purchase" && !secondLien;
  const subjectAmount = values.loanProgram === "heloc" ? values.lineAmount : values.loanAmount;
  const cashOutAmountLimit = values.loanProgram === "heloc" ? values.drawAmount : subjectAmount;

  add(
    !isIntegerInRange(values.fico, CREDIT_SCORE_MIN, CREDIT_SCORE_MAX),
    "fico",
    `FICO must be a whole number between ${CREDIT_SCORE_MIN} and ${CREDIT_SCORE_MAX}.`,
  );
  add(!isPositive(values.propertyValue), "propertyValue", "Home value must be greater than $0.");
  if (values.appraisalActive && !isBlank(values.appraisedValue)) {
    add(!isPositive(values.appraisedValue), "appraisedValue", "Appraised value must be greater than $0.");
  }

  if (purchaseFirstLien) {
    add(!isNonNegative(values.downPayment), "downPayment", "Down payment must be $0 or greater.");
    if (isFiniteNumber(values.downPayment) && isFiniteNumber(values.propertyValue)) {
      add(
        number(values.downPayment) >= number(values.propertyValue),
        "downPayment",
        "Down payment must be less than Home value.",
      );
    }
  }
  add(!isPositive(subjectAmount), values.loanProgram === "heloc" ? "lineAmount" : "loanAmount", "Loan amount must be greater than $0.");

  if (values.loanProgram === "heloc") {
    add(!isBlank(values.drawTermMonths) && !Number.isInteger(Number(values.drawTermMonths)), "drawTermMonths", "Draw term must be a whole number of months.");
    add(!isNonNegative(values.drawAmount), "drawAmount", "Draw amount must be $0 or greater.");
    if (isFiniteNumber(values.drawAmount) && isFiniteNumber(values.lineAmount)) {
      add(number(values.drawAmount) > number(values.lineAmount), "drawAmount", "Draw amount cannot exceed the line amount.");
    }
  }
  if (secondLien) {
    add(!isNonNegative(values.primaryMortgageAmount), "primaryMortgageAmount", "First mortgage amount must be $0 or greater.");
  }
  if (values.advancedOpen) {
    add(!isNonNegative(values.secondaryLoanAmount), "secondaryLoanAmount", "Subordinate loan balance must be $0 or greater.");
    add(!isPositive(values.totalLoanAmount), "totalLoanAmount", "Total loan must be greater than $0.");
    if (isFiniteNumber(values.totalLoanAmount) && isFiniteNumber(values.loanAmount)) {
      add(
        number(values.totalLoanAmount) < number(values.loanAmount),
        "totalLoanAmount",
        "Total loan cannot be less than the base loan amount.",
      );
    }
  }

  if (values.loanPurpose === "CashOutRefi") {
    add(!isPositive(values.cashOutAmount), "cashOutAmount", "Cash-Out amount must be greater than $0.");
    if (isFiniteNumber(values.cashOutAmount) && isFiniteNumber(cashOutAmountLimit)) {
      add(number(values.cashOutAmount) > number(cashOutAmountLimit), "cashOutAmount", "Cash-Out amount cannot exceed the new loan amount.");
    }
  }

  add(!isIntegerInRange(values.units, 1, 4), "units", "Units must be a whole number between 1 and 4.");
  if (isIntegerInRange(values.units, 1, 4)) {
    if (values.propertyType === "twotofourfamily") {
      add(number(values.units) < 2, "units", "A 2–4 Unit Multifamily property must have 2 to 4 units.");
    } else {
      add(number(values.units) !== 1, "units", "The selected property type must have exactly 1 unit.");
    }
  }
  add(!values.state, "state", "State is required.");
  add(!values.county, "county", "County is required.");
  if (!isBlank(values.zip)) {
    add(!zip5(values.zip), "zip", "ZIP must be 5 digits or ZIP+4.");
    // zipCounties: the ZIP's counties from lookupZipCounties, when known.
    if (zip5(values.zip) && values.state && values.county) {
      const counties = values.zipCounties;
      add(
        !zipMatchesLocation(counties, values.state, values.county),
        "zip",
        `ZIP ${zip5(values.zip)} is not in ${describeLocation(values.state, values.county)}. `
          + `It is in ${(counties || []).map((c) => describeLocation(c.state, c.county)).join(" or ")}.`
          + " Change the ZIP or the county.",
      );
    }
  }

  add(!isNonNegative(values.monthlyIncome), "monthlyIncome", "Monthly income must be $0 or greater.");
  add(!isNonNegative(values.monthlyDebt), "monthlyDebt", "Monthly debt must be $0 or greater.");
  add(!isNonNegative(values.reserves), "reserves", "Reserves must be 0 or greater.");
  add(!isIntegerInRange(values.propertiesFinanced, 0, Number.MAX_SAFE_INTEGER), "propertiesFinanced", "Properties financed must be a non-negative whole number.");
  const lockDays = values.lockDays ?? values.lockDay;
  add(!isIntegerInRange(lockDays, 1, Number.MAX_SAFE_INTEGER), "lockDays", "Lock days must be a positive whole number.");

  if (values.searchBy === "price") {
    add(!isFiniteNumber(values.targetPrice) || number(values.targetPrice) < 80 || number(values.targetPrice) > 120, "targetPrice", "Target price must be between 80 and 120.");
    add(!Array.isArray(values.targetPriceType) || values.targetPriceType.length === 0, "targetPriceType", "Select at least one target price component.");
  } else {
    add(!isFiniteNumber(values.targetRate) || number(values.targetRate) < 0 || number(values.targetRate) > 20, "targetRate", "Target rate must be between 0 and 20.");
  }
  if (values.compensationType === "borrower_paid") {
    add(!isNonNegative(values.compensationPercent), "compensationPercent", "Borrower-paid compensation must be 0% or greater.");
  }

  if (values.loanProgram === "va" && !values.vaExempt) {
    add(!isPositive(values.fundingFee) || number(values.fundingFee) > 100, "fundingFee", "VA Funding Fee must be greater than 0% and no more than 100%.");
  }
  if (values.loanProgram === "usda") {
    add(!isNonNegative(values.guaranteeFee) || number(values.guaranteeFee) > 100, "guaranteeFee", "USDA G-Fee must be between 0% and 100%.");
  }

  if (values.advancedOpen) {
    [["cltv", values.cltvOverride], ["hcltv", values.hcltvOverride]].forEach(([field, value]) => {
      if (!isBlank(value)) {
        add(!isPositive(value) || number(value) > 200, field, `${field.toUpperCase()} must be greater than 0% and no more than 200%.`);
      }
    });
  }
  [["LTV", values.ltv], ["CLTV", values.cltv], ["HCLTV", values.hcltv]].forEach(([field, value]) => {
    const allowZero = values.loanProgram === "heloc";
    const invalid = allowZero
      ? !isNonNegative(value) || number(value) > 200
      : !isPositive(value) || number(value) > 200;
    add(invalid, field.toLowerCase(), `${field} must be ${allowZero ? "0% or greater" : "greater than 0%"} and no more than 200%.`);
  });

  if (values.requiresDscr && values.dscrMode !== "noRatio") {
    add(!isPositive(values.dscr), "dscr", "DSCR must be greater than 0.");
  }
  (values.lateCounts || []).forEach((value) => {
    add(!isIntegerInRange(value, 0, Number.MAX_SAFE_INTEGER), "mortgageLates", "Mortgage late counts must be non-negative whole numbers.");
  });

  return { valid: errors.length === 0, errors, firstError: errors[0] };
}
